/************************************** THIRD-PARTY MODULES ***************************************/
import {
    isInt,
    isAbsoluteURL,
    isBoolean,
    RealAny,
    RouterProps,
    RRHistory,
} from 'mad-utils/lib/react';
import {stringify as qsStringify, parse as qsParse} from 'query-string';

/**************************************** PROJECT MODULES *****************************************/
import {getLangFromURL} from 'mad-utils/lib/react';
import {StoreProps, Store, translate} from '@canimmunize/core-imports';

/******************************************** EXPORTS *********************************************/
/**
 * Ensure a Record is selected
 * If no record is explicitly set in the query params, selects which Record to
 * render based on other criteria, then updates the query param accordingly
 */
export const ensureCurrentRecordSet = (props: StoreProps & RouterProps) => {
    const {recordModelsStore, activeRecordModel, recordModels} = props.appState;
    const {activeRecordIdx} = recordModelsStore;

    const queryParams = qsParse(window.location.search);

    // Do nothing if no records defined.
    if (!recordModels || recordModels.length === 0) return;

    // Handle no current_record in query params (select record & add it to query params)
    if (!queryParams.current_record) {
        queryParams.current_record =
            activeRecordIdx || isInt(activeRecordIdx)
                ? // If active record + no query params, get activeRecordModel from appState
                  activeRecordModel.fullNameQueryParam
                : // If no active record + no query params, select 1st stored record
                  recordModels[0].fullNameQueryParam;

        props.history.push(`record-settings?${qsStringify(queryParams)}`);
    }
};

/**
 * Swap pathname in URL at 4rd position after host
 * Preserve query params & all other paths
 *
 * Example: if URL is [HOST]/a/b/c/d, we get [HOST]/a/b/GR/d if we run
 *          navToNewThirdPathInURL(`GR`)
 *
 * @param {string} newThirdPath Swap third path with this string
 * @return {string} URL with third path swapped out
 *                      Also mutates the current URL
 */
export const navToNewThirdPathInURL = (props: {
    newPath: string;
    history: RRHistory;
    queryParams?: string | Record<string, string>;
}): string => {
    // Handle query params if given no props.queryParam value
    let qps: string;
    if (typeof props.queryParams === `undefined` || props.queryParams === null) {
        qps = window.location.search.split(`?`).join(``);

        // Handle query params if props.queryParam value is a string
    } else if (typeof props.queryParams === `string`) {
        qps = props.queryParams;

        // Handle case where query params is an object
    } else {
        qps = qsStringify(props.queryParams);
    }

    // Get safe query param string, with no `?` at the 1st char guaranteed
    const cleanQps = qps.split(`?`).join(``);

    // Build new URL
    const newURL = `/main/${getLangFromURL()}/${props.newPath}?${cleanQps}`;

    // Push then return new URL
    props.history.push(newURL);
    return newURL;
};

/**
 * Open link in a new window/tab
 * @param {string} url Location to open link to
 */
export const openLinkInNewTab = (url: string) => ev => {
    ev.preventDefault();
    ev.stopPropagation();
    const cleanUrl = url.match(/^http/) ? url : `https://${url}`;
    window.open(cleanUrl, `_blank`);
};

function performNavigation(
    historyOrNavAction: RRHistory | (() => RealAny),
    route?: string,
    preNavAction?: () => RealAny
) {
    if (typeof historyOrNavAction === `function`) {
        historyOrNavAction();
    } else {
        if (preNavAction) preNavAction();
        if (isAbsoluteURL(route)) {
            window.location.href = route;
        } else if (route) {
            historyOrNavAction.push(route);
        }
    }
}

/**
 * If no unsaved data detected in root store [appState], run [preNavAction],
 * then use React Router [history] object to navigate to given [route] {string}
 * @param {string} route String route to navigate to if unsaved data is present
 */
export function navIfNoUnsavedData(
    appState: Store,
    history: RRHistory,
    route: string,
    preNavAction: () => RealAny
): boolean;

/**
 * If no unsaved data detected in root store [appState], run [navigationAction]
 * function
 * @param {string} route String route to navigate to if unsaved data is present
 */
export function navIfNoUnsavedData(
    appState: Store,
    navigationAction: () => RealAny,
    preventShutoffUnsaved?: boolean
): boolean;

/**
 * If no unsaved data detected in root store [appState], use React Router [history]
 * object to navigate to given [route] {string}
 *
 * Returns true if navigation occurred, false if not (if there was unsaved data)
 * @param {string} route String route to navigate to if unsaved data is present
 */
export function navIfNoUnsavedData(appState: Store, history: RRHistory, route: string): boolean;

export function navIfNoUnsavedData(
    appState: Store,
    historyOrNavAction: RRHistory | (() => RealAny),
    routeOrPreventShutoffUnsaved?: string | boolean,
    preNavAction?: () => RealAny
): boolean {
    /*
     * Can only be set to true by this signature:
     *   navIfNoUnsavedData(
     *       appState: Store,
     *       navigationAction: () => RealAny,
     *       preventShutoffUnsaved?: boolean
     *   ): boolean
     */
    let preventShutoffUnsaved = isBoolean(routeOrPreventShutoffUnsaved)
        ? routeOrPreventShutoffUnsaved
        : false;

    /* Set to string value for all other routes but the preceding */
    const route = isBoolean(routeOrPreventShutoffUnsaved) ? null : routeOrPreventShutoffUnsaved;

    // If unsaved data, get confirmation from user before navigating
    if (appState.hasUnsavedData) {
        if (window.confirm(translate(`warning_unsaved_changes`))) {
            performNavigation(historyOrNavAction, route, preNavAction);
            if (!preventShutoffUnsaved) appState.setHasUnsavedData(false);
            return true;
        }
        return false;
    }

    // If no unsaved data, navigate
    performNavigation(historyOrNavAction, route, preNavAction);
    return true;
}
